/* eslint-disable no-case-declarations */
import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  ManagementState,
  TotalTasksData,
  TotalProgressData,
  TotalFinishedData,
} from 'store/modules/management_store/types';

interface DoughnutGraphicProps {
  featureId: string;
  featureLabel: string;
  isActive: boolean;
}

interface MultipleData {
  label: string;
  data: number;
  backgroundColor: string;
}

const DoughnutGraphic = (props: DoughnutGraphicProps) => {
  ChartJS.register(ArcElement, Tooltip, Title);
  const { featureId, featureLabel, isActive } = props;
  const [showTooltips, setShowToolTips] = useState<boolean>(true);

  const { management } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const valueOrZero = (total: number) => {
    if (!showTooltips) return 100;

    if (total > 0) return total;

    setShowToolTips(false);
    return 100;
  };

  const colorOrGray = (total: number, feature: string) => {
    if (total === 0) return '#b3b8bf';

    if (feature === 'favorites') return '#d23351';

    return '#66a33c';
  };

  const tasksData = (total_tasks: TotalTasksData) => {
    let data: Array<MultipleData> = [];
    if (total_tasks.future > 0)
      data = [...data, { label: 'Futuras', data: total_tasks.future, backgroundColor: '#17a2b8' }];
    if (total_tasks.late > 0)
      data = [...data, { label: 'Atrasadas', data: total_tasks.late, backgroundColor: '#dc3545' }];
    if (total_tasks.today > 0)
      data = [...data, { label: 'Hoje', data: total_tasks.today, backgroundColor: '#F0AD4E' }];

    return data;
  };

  const progressData = (total_progress: TotalProgressData) => {
    let data: Array<MultipleData> = [];
    if (total_progress.read > 0)
      data = [...data, { label: 'Lidas', data: total_progress.read, backgroundColor: '#28a745' }];
    if (total_progress.unread > 0)
      data = [
        ...data,
        { label: 'Não Lidas', data: total_progress.unread, backgroundColor: '#F0AD4E' },
      ];

    return data;
  };

  const finishedData = (total_finished: TotalFinishedData) => {
    let data: Array<MultipleData> = [];
    if (total_finished.winner > 0)
      data = [
        ...data,
        {
          label: 'Vencedor em todos os itens',
          data: total_finished.winner,
          backgroundColor: '#01A87A',
        },
      ];
    if (total_finished.partial_winner > 0)
      data = [
        ...data,
        {
          label: 'Vencedor em alguns itens',
          data: total_finished.partial_winner,
          backgroundColor: '#18A8DB',
        },
      ];
    if (total_finished.lost > 0)
      data = [
        ...data,
        { label: 'Nenhum item ganho', data: total_finished.lost, backgroundColor: '#DB1846' },
      ];
    if (total_finished.declined > 0)
      data = [
        ...data,
        {
          label: 'Declinamos da participação',
          data: total_finished.declined,
          backgroundColor: '#FDD817',
        },
      ];
    if (total_finished.revoked > 0)
      data = [
        ...data,
        {
          label: 'A licitação foi revogada',
          data: total_finished.revoked,
          backgroundColor: '#7A5D06',
        },
      ];
    if (total_finished.failed > 0)
      data = [
        ...data,
        {
          label: 'A licitação foi fracassada',
          data: total_finished.failed,
          backgroundColor: '#FDA117',
        },
      ];
    if (total_finished.cancelled > 0)
      data = [
        ...data,
        {
          label: 'A licitação foi anulada',
          data: total_finished.cancelled,
          backgroundColor: '#956CCA',
        },
      ];

    return data;
  };

  const dataFactory = () => {
    let labels: Array<string> = [];
    let data: Array<number | string> = [];
    let backgroundColor: Array<string> = [];
    let text: string = '';

    switch (featureId) {
      case 'favorites':
        labels = [featureLabel];
        data = [valueOrZero(management.management_summary.total_favorites)];
        backgroundColor = [colorOrGray(management.management_summary.total_favorites, featureId)];
        text = management.management_summary.total_favorites.toString();
        break;

      case 'managed':
        labels = [featureLabel];
        data = [valueOrZero(management.management_summary.total_managed)];
        backgroundColor = [colorOrGray(management.management_summary.total_managed, featureId)];
        text = management.management_summary.total_managed.toString();
        break;

      case 'tasks':
        const totalTasks =
          management.management_summary.total_tasks.future +
          management.management_summary.total_tasks.late +
          management.management_summary.total_tasks.today;

        if (tasksData(management.management_summary.total_tasks).length === 0) {
          labels = [''];
          data = [valueOrZero(totalTasks)];
          backgroundColor = ['#b3b8bf'];
          text = '0';
          break;
        }
        labels = tasksData(management.management_summary.total_tasks).map(
          (taskData) => taskData.label,
        );
        data = tasksData(management.management_summary.total_tasks).map(
          (taskData) => taskData.data,
        );
        backgroundColor = tasksData(management.management_summary.total_tasks).map(
          (taskData) => taskData.backgroundColor,
        );
        text = totalTasks.toString();
        break;

      case 'progress':
        const totalProgress =
          management.management_summary.total_progress.read +
          management.management_summary.total_progress.unread;

        if (progressData(management.management_summary.total_progress).length === 0) {
          labels = [''];
          data = [valueOrZero(totalProgress)];
          backgroundColor = ['#b3b8bf'];
          text = '0';
          break;
        }
        labels = progressData(management.management_summary.total_progress).map(
          (progressData) => progressData.label,
        );
        data = progressData(management.management_summary.total_progress).map(
          (progressData) => progressData.data,
        );
        backgroundColor = progressData(management.management_summary.total_progress).map(
          (progressData) => progressData.backgroundColor,
        );
        text = totalProgress.toString();
        break;

      case 'finished':
        const totalFinished =
          management.management_summary.total_finished.winner +
          management.management_summary.total_finished.partial_winner +
          management.management_summary.total_finished.lost +
          management.management_summary.total_finished.declined +
          management.management_summary.total_finished.revoked +
          management.management_summary.total_finished.failed +
          management.management_summary.total_finished.cancelled;

        if (finishedData(management.management_summary.total_finished).length === 0) {
          labels = [''];
          data = [valueOrZero(totalFinished)];
          backgroundColor = ['#b3b8bf'];
          text = '0';
          break;
        }
        labels = finishedData(management.management_summary.total_finished).map(
          (finishedData) => finishedData.label,
        );
        data = finishedData(management.management_summary.total_finished).map(
          (finishedData) => finishedData.data,
        );
        backgroundColor = finishedData(management.management_summary.total_finished).map(
          (finishedData) => finishedData.backgroundColor,
        );
        text = totalFinished.toString();
        break;

      default:
        break;
    }

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 0,
          showTooltips: false,
        },
      ],
      text,
      fillStyle: isActive ? '#fff' : '#000',
    };
  };

  const plugins = [
    {
      id: '1',
      beforeDraw(chart: any) {
        const { width } = chart;
        const { height } = chart;
        const { ctx } = chart;
        ctx.restore();
        ctx.font = '2em Roboto, sans-serif';
        ctx.fillStyle = dataFactory().fillStyle;
        ctx.textBaseline = 'middle';
        const { text } = dataFactory();
        const textX = Math.round((width - ctx.measureText(text).width) / 2);
        const textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      },
    },
  ];

  const doughnutOptions = {
    cutout: 52,
    legend: {
      display: false,
    },
    layout: {
      padding: 10,
    },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: showTooltips,
      },
      legend: {
        display: false,
      },
    },
  };

  return <Doughnut data={dataFactory()} plugins={plugins} options={doughnutOptions} />;
};

export default DoughnutGraphic;
